import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BellAlertIcon,
  BellSlashIcon,
  CameraIcon,
  ChartBarSquareIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline'
import { Text, Button, Heading } from '../ui'
import { useTheme } from '../../hooks/useTheme'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useNotifications } from '../../hooks/useNotifications'
import { modulesService } from '../../services/modules'
import { getSignedAvatarUrl } from '../../services/avatar'
import { showBrowserNotification } from '../../utils/browserNotifications'
import type { ModuleWithPermissions } from '../../types'
import { getModuleIcon } from '../../utils/moduleIcons'
import { departmentLabel } from '../../utils/userDisplayName'

interface DashboardWelcomeHeaderProps {
  onOpenUserSettings: () => void
  onOpenQgNumeros?: () => void
}

const findPasiGptModule = (modules: ModuleWithPermissions[] | undefined) =>
  modules?.find(
    (m) => m.can_show && (m.name === 'pasigpt' || m.route === '/pasigpt')
  )

function getGreetingPrefix(date = new Date()): string {
  const hour = date.getHours()
  if (hour >= 5 && hour < 12) return 'Bom dia'
  if (hour >= 12 && hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

function getFirstName(fullName?: string | null): string {
  if (!fullName?.trim()) return 'colaborador'
  return fullName.trim().split(/\s+/)[0]
}

function getUserFirstName(profileName?: string | null, email?: string | null, metadata?: Record<string, unknown>): string {
  const metadataName =
    typeof metadata?.name === 'string'
      ? metadata.name
      : typeof metadata?.full_name === 'string'
        ? metadata.full_name
        : typeof metadata?.display_name === 'string'
          ? metadata.display_name
          : null

  if (profileName?.trim()) return getFirstName(profileName)
  if (metadataName?.trim()) return getFirstName(metadataName)
  if (email?.trim()) return email.split('@')[0]
  return 'colaborador'
}

function formatWelcomeDate(date = new Date()): string {
  const raw = date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

function getInitials(text: string): string {
  if (!text) return 'U'
  const words = text.trim().split(/\s+/)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return text.substring(0, 2).toUpperCase()
}

const shortcutButtonClass =
  'shrink-0 border-gray-300 dark:border-gray-600 !px-2.5 !py-1.5 text-xs sm:text-sm'

const primaryShortcutButtonClass =
  'shrink-0 !px-2.5 !py-1.5 text-xs sm:text-sm border-0 !bg-primary-500 !text-white shadow-sm ring-0 hover:!bg-primary-600 focus:ring-primary-500/25 dark:!bg-primary-500 dark:!text-white dark:hover:!bg-primary-400 dark:focus:ring-primary-400/30'

const DashboardWelcomeHeader: React.FC<DashboardWelcomeHeaderProps> = ({
  onOpenUserSettings,
  onOpenQgNumeros,
}) => {
  const navigate = useNavigate()
  const { profile, user } = useAuthContext()
  const { darkMode, toggleDarkMode } = useTheme()
  const { browserPermission, requestBrowserPermission } = useNotifications()
  const { hasPermission: canViewQgNumeros, loading: loadingQgNumerosPermission } = usePermission(
    'dashboard',
    'view_values'
  )
  const [pasiGptModule, setPasiGptModule] = useState<ModuleWithPermissions | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [requestingBrowserPermission, setRequestingBrowserPermission] = useState(false)

  const firstName = useMemo(
    () => getUserFirstName(profile?.name, user?.email ?? null, user?.user_metadata as Record<string, unknown> | undefined),
    [profile?.name, user?.email, user?.user_metadata]
  )
  const departmentName = departmentLabel(profile?.department)
  const greeting = useMemo(
    () => `${getGreetingPrefix()}, ${departmentName ? `${firstName} (${departmentName})` : firstName} 👋`,
    [firstName, departmentName],
  )
  const subtitle = useMemo(() => formatWelcomeDate(), [])
  const displayName = profile?.name || user?.email || 'Usuário'
  const hasProfilePhoto = Boolean(profile?.avatar_url)
  const showQgNumeros = canViewQgNumeros && !loadingQgNumerosPermission
  const showBrowserNotificationsButton =
    browserPermission === 'default' || browserPermission === 'denied'

  useEffect(() => {
    let cancelled = false

    const loadPasiGptAccess = async () => {
      if (!profile?.user_group_id) {
        if (!cancelled) setPasiGptModule(null)
        return
      }

      const { data } = await modulesService.getActiveWithPermissionsByGroup(profile.user_group_id)
      if (!cancelled) {
        setPasiGptModule(findPasiGptModule(data) ?? null)
      }
    }

    loadPasiGptAccess()
    return () => {
      cancelled = true
    }
  }, [profile?.user_group_id])

  useEffect(() => {
    let cancelled = false

    const loadAvatar = async () => {
      if (!profile?.avatar_url) {
        if (!cancelled) setAvatarUrl(null)
        return
      }
      const url = await getSignedAvatarUrl(profile.avatar_url)
      if (!cancelled) setAvatarUrl(url)
    }

    loadAvatar()
    return () => {
      cancelled = true
    }
  }, [profile?.avatar_url])

  const handleEnableBrowserNotifications = async () => {
    if (browserPermission !== 'default') return
    setRequestingBrowserPermission(true)
    try {
      const result = await requestBrowserPermission()
      if (result === 'granted') {
        showBrowserNotification({
          title: 'PASI QG',
          body: 'Notificações do navegador ativadas.',
          tag: 'qg-browser-notifications-enabled',
        })
      }
    } finally {
      setRequestingBrowserPermission(false)
    }
  }

  const showPasiGpt = Boolean(pasiGptModule)
  const PasiGptIcon = getModuleIcon(pasiGptModule?.icon ?? 'chat-bubble-left-right')

  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between pt-4 sm:pt-5">
      <div className="min-w-0">
        <Heading as="h1" size="2xl" className="text-gray-900 dark:text-white tracking-tight">
          {greeting}
        </Heading>
        <Text size="sm" color="muted" className="mt-1">
          {subtitle}
        </Text>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:justify-end shrink-0">
        {showBrowserNotificationsButton && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleEnableBrowserNotifications}
            disabled={requestingBrowserPermission || browserPermission === 'denied'}
            className={shortcutButtonClass}
            aria-label={
              browserPermission === 'denied'
                ? 'Notificações do navegador bloqueadas'
                : 'Ativar notificações do navegador'
            }
            title={
              browserPermission === 'denied'
                ? 'Bloqueado nas configurações do navegador'
                : undefined
            }
          >
            {browserPermission === 'denied' ? (
              <BellSlashIcon className="h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <BellAlertIcon className="h-4 w-4 shrink-0" aria-hidden />
            )}
            {browserPermission === 'denied' ? 'Notificações bloqueadas' : 'Ativar notificações'}
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleDarkMode}
          className={shortcutButtonClass}
          aria-label={darkMode ? 'Alternar para modo claro' : 'Alternar para modo escuro'}
        >
          {darkMode ? (
            <SunIcon className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <MoonIcon className="h-4 w-4 shrink-0" aria-hidden />
          )}
          Aparência
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onOpenUserSettings}
          className={shortcutButtonClass}
          aria-label={hasProfilePhoto ? 'Alterar foto de perfil' : 'Adicionar foto de perfil'}
        >
          {avatarUrl ? (
            <span className="h-5 w-5 shrink-0 rounded-full overflow-hidden ring-1 ring-gray-200 dark:ring-gray-600">
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            </span>
          ) : (
            <span className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary-500 to-primary-700 text-[9px] font-semibold text-white">
              {getInitials(displayName)}
              <span className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-600">
                <CameraIcon className="h-1.5 w-1.5 text-gray-600 dark:text-gray-400" aria-hidden />
              </span>
            </span>
          )}
          Foto do perfil
        </Button>

        {showPasiGpt && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/pasigpt')}
            className={shortcutButtonClass}
          >
            <PasiGptIcon className="h-4 w-4 shrink-0" aria-hidden />
            {pasiGptModule?.display_name ?? 'PasiGPT'}
          </Button>
        )}

        {showQgNumeros && onOpenQgNumeros && (
          <Button
            type="button"
            variant="solid"
            size="sm"
            onClick={onOpenQgNumeros}
            className={primaryShortcutButtonClass}
          >
            <ChartBarSquareIcon className="h-4 w-4 shrink-0" aria-hidden />
            Números PASI QG
          </Button>
        )}
      </div>
    </header>
  )
}

export default DashboardWelcomeHeader
